/**
 * =============================================================================
 * useTenantBrand.ts - Hook per branding dinamico basato sul tenant
 * =============================================================================
 *
 * Fornisce nome, slug, flag isLni e parametri white-label (colori, logo,
 * favicon) per tutti i componenti UI. Centralizza la logica di branding.
 *
 * PRECEDENZA DI RISOLUZIONE (Fase 3):
 *   1. override ?tenant=<slug> (simulatore, solo DEV/QA);
 *   2. tenant dell'utente autenticato (profiles.tenant_id);
 *   3. dominio custom mappato in `tenant_domains` (RPC resolve_tenant_by_host);
 *   4. fallback per host (LNI su lni-formazione.it/anteprima, altrimenti neutro).
 *
 * @version 1.2.0 - Mapping domini custom e configurazione white-label
 * =============================================================================
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenantId } from "@/hooks/useTenantId";
import { getFullBrandName } from "@/config/brandConfig";
import { getTenantOverrideSlug } from "@/lib/tenantSimulator";

/** Parametri white-label opzionali letti da `tenant_configs`. */
export interface TenantBrandAssets {
  organizationName: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
}

export interface TenantBrand extends TenantBrandAssets {
  /** ID del tenant risolto, usato anche per associare la registrazione. */
  tenantId: string | null;
  /** Nome del tenant (es. "Scuola Nautica Beta") */
  name: string;
  /** Slug del tenant (es. "beta", "lni") */
  slug: string | null;
  /** true per il tenant LNI risolto, oppure per il fallback su un host LNI. */
  isLni: boolean;
  /** Nome da mostrare nella UI */
  displayName: string;
  /** Testo copyright dinamico */
  copyrightText: string;
  /** Se i dati sono stati caricati */
  isLoaded: boolean;
  /** Origine della risoluzione, utile per debug/QA */
  source: "override" | "user" | "domain" | "host";
}

/** Nome del brand neutro usato sui domini non LNI (SaaS e-learning). */
export const NEUTRAL_BRAND_NAME = "Istruiamo";

/**
 * Normalizza un hostname per il confronto con `tenant_domains.domain`:
 * minuscolo, senza spazi, senza porta, senza punto finale e senza "www.".
 * Deve restare allineato alla normalizzazione SQL di resolve_tenant_by_host.
 */
export function normalizeHost(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/:\d+$/, "").replace(/\.$/, "").replace(/^www\./, "");
}

/** Tenant configurato per registrazioni locali; mai usato su host pubblici. */
export function getLocalDevelopmentTenantId(): string | null {
  const host = typeof window !== "undefined" ? normalizeHost(window.location.hostname) : "";
  if (!import.meta.env.DEV || (host !== "localhost" && host !== "127.0.0.1")) {
    return null;
  }

  const tenantId = import.meta.env.VITE_DEFAULT_TENANT_ID?.trim();
  return tenantId && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(tenantId)
    ? tenantId
    : null;
}

/**
 * Rilevamento per host (fallback finale, Fase 2 - neutralità del dominio).
 * - lni-formazione.it o suoi sottodomini (match esatto o delimitato da punto) → LNI;
 * - host di sviluppo/anteprima (localhost, *.lovable.app, *.lovableproject.com) → LNI;
 * - qualsiasi altro host (es. istruiamo.app) → neutro "Istruiamo".
 */
export function isLniHost(hostname: string = typeof window !== "undefined" ? window.location.hostname : ""): boolean {
  const h = normalizeHost(hostname);
  if (h === "lni-formazione.it" || h.endsWith(".lni-formazione.it")) return true;
  if (!h || h === "localhost" || h === "127.0.0.1") return true;
  if (h.endsWith(".lovable.app") || h.endsWith(".lovableproject.com")) return true;
  return false;
}

/** Riga restituita dalla risoluzione (tenant + config opzionale). */
interface ResolvedTenant extends TenantBrandAssets {
  tenantId: string;
  name: string;
  slug: string;
  source: "override" | "user" | "domain";
}

/** Legge i parametri white-label di un tenant (null se non leggibili per RLS). */
async function fetchAssets(tenantId: string): Promise<TenantBrandAssets> {
  const { data } = await supabase
    .from("tenant_configs")
    .select("organization_name, primary_color, secondary_color, accent_color, logo_url, favicon_url")
    .eq("tenant_id", tenantId)
    .maybeSingle();
  return {
    organizationName: data?.organization_name ?? null,
    primaryColor: data?.primary_color ?? null,
    secondaryColor: data?.secondary_color ?? null,
    accentColor: data?.accent_color ?? null,
    logoUrl: data?.logo_url ?? null,
    faviconUrl: data?.favicon_url ?? null,
  };
}

export function useTenantBrand(): TenantBrand {
  const tenantId = useTenantId();
  // Override "simulazione tenant" (solo in DEV o con flag esplicito).
  const overrideSlug = getTenantOverrideSlug();
  const host = typeof window !== "undefined" ? normalizeHost(window.location.hostname) : "";

  const { data: tenantData, isSuccess } = useQuery({
    queryKey: ["tenant_brand_central", tenantId, overrideSlug, host],
    queryFn: async (): Promise<ResolvedTenant | null> => {
      // 1) Override slug del simulatore.
      if (overrideSlug) {
        const { data } = await supabase.from("tenants").select("id, name, slug").eq("slug", overrideSlug).maybeSingle();
        if (data) return { tenantId: data.id, name: data.name, slug: data.slug, source: "override", ...(await fetchAssets(data.id)) };
      }
      // 2) Tenant dell'utente autenticato.
      if (tenantId) {
        const { data } = await supabase.from("tenants").select("id, name, slug").eq("id", tenantId).maybeSingle();
        if (data) return { tenantId: data.id, name: data.name, slug: data.slug, source: "user", ...(await fetchAssets(data.id)) };
      }
      // 3) Dominio custom: RPC pubblica che espone solo i campi di branding.
      if (host) {
        const { data } = await supabase.rpc("resolve_tenant_by_host", { _host: host });
        const row = Array.isArray(data) ? data[0] : null;
        if (row) {
          return {
            tenantId: row.tenant_id,
            name: row.name,
            slug: row.slug,
            source: "domain",
            organizationName: row.organization_name,
            primaryColor: row.primary_color,
            secondaryColor: row.secondary_color,
            accentColor: row.accent_color,
            logoUrl: row.logo_url,
            faviconUrl: row.favicon_url,
          };
        }
      }
      return null;
    },
    staleTime: Infinity,
  });

  // Tenant risolto → decide lo slug; altrimenti fallback per host.
  const hostIsLni = isLniHost();
  const localDevelopmentTenantId = getLocalDevelopmentTenantId();
  const isLni = tenantData ? tenantData.slug === "lni" : hostIsLni;
  const name = tenantData?.organizationName || tenantData?.name || (hostIsLni ? "LNI" : NEUTRAL_BRAND_NAME);
  const displayName = isLni ? getFullBrandName() : name;
  const copyrightText = isLni
    ? `© ${new Date().getFullYear()} LNI Formazione. Tutti i diritti riservati.`
    : `© ${new Date().getFullYear()} ${name}. Tutti i diritti riservati.`;

  return {
    tenantId: tenantData?.tenantId ?? localDevelopmentTenantId,
    name,
    slug: tenantData?.slug ?? null,
    isLni,
    displayName,
    copyrightText,
    isLoaded: isSuccess,
    source: tenantData?.source ?? "host",
    organizationName: tenantData?.organizationName ?? null,
    primaryColor: tenantData?.primaryColor ?? null,
    secondaryColor: tenantData?.secondaryColor ?? null,
    accentColor: tenantData?.accentColor ?? null,
    logoUrl: tenantData?.logoUrl ?? null,
    faviconUrl: tenantData?.faviconUrl ?? null,
  };
}
